/**
 * RD Consent Manager v2
 * 
 * Self-contained consent management with localStorage persistence
 * and backend logging via WP REST API.
 * 
 * No external CMP dependency - we own the entire consent flow.
 */

import { getService, getBlockerConfig, getServiceCategory, getServicesForCategory, SITE_CONFIG } from './services-config';

// Consent categories
export type ConsentCategory = 'necessary' | 'marketing' | 'analytics' | 'external-media' | 'functionality';

export type ConsentState = Partial<Record<ConsentCategory, boolean>>;

const ACCENT = (SITE_CONFIG as any).accentColor || 'rgb(82, 197, 255)';

// Inject blocker styles once
let blockerStylesInjected = false;
function injectBlockerStyles(): void {
  if (blockerStylesInjected) return;
  blockerStylesInjected = true;
  
  const style = document.createElement('style');
  style.id = 'rd-consent-blocker-styles';
  style.textContent = `
.rd-consent-dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
  padding: 20px;
}
.rd-consent-dialog {
  background: #fff;
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}
.rd-consent-dialog-inline {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}
.rd-consent-dialog-body {
  padding: 24px;
}
.rd-consent-level1 {
  font-size: 15px;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0 0 12px;
  line-height: 1.5;
}
.rd-consent-level2 {
  font-size: 13px;
  color: #666;
  margin: 0 0 16px;
  line-height: 1.5;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #e5e7eb;
}
.rd-consent-level3 {
  font-size: 14px;
  color: #374151;
  margin: 0;
  line-height: 1.5;
}
.rd-consent-dialog-actions {
  display: flex;
  gap: 12px;
  padding: 0 24px 20px;
}
.rd-consent-dialog-accept {
  flex: 1;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  background: ${ACCENT};
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.rd-consent-dialog-accept:hover {
  filter: brightness(0.9);
}
.rd-consent-dialog-decline {
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.rd-consent-dialog-decline:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
.rd-consent-dialog-footer {
  padding: 12px 24px;
  background: #f8f9fa;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  color: #6b7280;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.rd-consent-dialog-footer a {
  color: ${ACCENT};
  text-decoration: none;
}
.rd-consent-dialog-footer a:hover {
  text-decoration: underline;
}
.rd-consent-withdraw-hint {
  color: #9ca3af;
}
.rd-consent-blocker {
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}
.rd-consent-blocker-dismissed {
  display: none;
}
@media (max-width: 480px) {
  .rd-consent-dialog-actions {
    flex-direction: column;
  }
  .rd-consent-dialog-footer {
    flex-direction: column;
    text-align: center;
  }
}
`;
  document.head.appendChild(style);
}

// Storage keys
const STORAGE_KEY = 'rd_consent';
const UUID_KEY = 'rd_consent_uuid';

// Default state - necessary is always true
const DEFAULT_STATE: ConsentState = {
  necessary: true,
  marketing: false,
  analytics: false,
  'external-media': false,
  functionality: false,
};

export type ConsentEventType = 'rd:consent:ready' | 'rd:consent:changed' | 'rd:consent:granted' | 'rd:consent:revoked';

export interface ConsentEvent {
  type: ConsentEventType;
  state: ConsentState;
  timestamp: number;
  category?: ConsentCategory;
  triggerElementId?: string | null;
}

type ConsentSubscriber = (state: ConsentState) => void;

class RDConsentManager {
  private subscribers = new Set<ConsentSubscriber>();
  private currentState: ConsentState = { ...DEFAULT_STATE };
  private ready = false;
  private uuid: string = '';
  private pendingConsentResolvers = new Map<string, { resolve: (granted: boolean) => void; triggerElementId: string | null }>();
  private loadedScripts = new Set<string>();  // Track loaded scripts to avoid duplicates
  private showCMPHandler: (() => void) | null = null;  // Handler for showing consent modal

  /**
   * Initialize consent manager - loads state from localStorage
   */
  async init(): Promise<void> {
    // Load or create UUID for this browser
    this.uuid = this.getOrCreateUUID();
    
    // Load state from localStorage
    this.currentState = this.loadState();
    this.ready = true;
    
    console.info('[ConsentManager] Initialized with state:', this.currentState);
    
    // Emit ready event
    this.emit('rd:consent:ready', this.currentState);
    
    // Fire grant events for categories that already have consent
    for (const category of Object.keys(this.currentState) as ConsentCategory[]) {
      if (this.currentState[category]) {
        this.emitGrant(category, null);
        // Load scripts for already-consented categories
        this.loadScriptsForCategory(category);
      }
    }
  }

  private getOrCreateUUID(): string {
    let uuid = localStorage.getItem(UUID_KEY);
    if (!uuid) {
      uuid = crypto.randomUUID();
      localStorage.setItem(UUID_KEY, uuid);
    }
    return uuid;
  }

  private loadState(): ConsentState {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all categories exist
        return { ...DEFAULT_STATE, ...parsed };
      }
    } catch (e) {
      console.warn('[ConsentManager] Failed to load state:', e);
    }
    return { ...DEFAULT_STATE };
  }

  private saveState(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.currentState));
    } catch (e) {
      console.warn('[ConsentManager] Failed to save state:', e);
    }
  }

  /**
   * Log consent change to backend
   * Returns a promise that resolves when the request completes (or fails)
   */
  private logToBackend(): Promise<void> {
    const endpoint = (SITE_CONFIG as any).restUrl || '/wp-json/rd/v1/consent';
    
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uuid: this.uuid,
        consent_state: this.currentState,
      }),
      credentials: 'same-origin',
    }).then(() => {
      // Success - nothing to do
    }).catch(e => {
      // Silent fail - consent still works locally
      console.warn('[ConsentManager] Failed to log consent to backend:', e);
    });
  }

  private emit(type: ConsentEventType, state: ConsentState, extra?: Partial<ConsentEvent>): void {
    const event: ConsentEvent = { type, state, timestamp: Date.now(), ...extra };
    document.dispatchEvent(new CustomEvent(type, { detail: event }));
  }

  private emitGrant(category: ConsentCategory, triggerElementId: string | null): void {
    this.emit('rd:consent:granted', this.currentState, { category, triggerElementId });
  }

  private emitRevoke(category: ConsentCategory): void {
    this.emit('rd:consent:revoked', this.currentState, { category });
  }

  private notifySubscribers(state: ConsentState): void {
    for (const fn of this.subscribers) {
      fn(state);
    }
  }

  /**
   * Load scripts defined in services-config for a given category
   * Scripts are loaded only once, even if called multiple times
   */
  private loadScriptsForCategory(category: ConsentCategory): void {
    const services = getServicesForCategory(category);
    
    for (const service of services) {
      if (!service.scripts || service.scripts.length === 0) continue;
      
      for (const scriptUrl of service.scripts) {
        if (this.loadedScripts.has(scriptUrl)) continue;
        
        this.loadedScripts.add(scriptUrl);
        console.info(`[ConsentManager] Loading script for ${service.id}: ${scriptUrl}`);
        
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.dataset.consentService = service.id;
        script.dataset.consentCategory = category;
        document.head.appendChild(script);
      }
    }
  }

  /** Wait for consent manager to be ready */
  whenReady(): Promise<void> {
    if (this.ready) return Promise.resolve();
    // If not ready yet, wait for init to complete
    return new Promise(resolve => {
      const check = () => {
        if (this.ready) resolve();
        else setTimeout(check, 10);
      };
      check();
    });
  }

  /** Check if consent manager is initialized */
  isReady(): boolean {
    return this.ready;
  }

  /** Get current consent state */
  getState(): ConsentState {
    return { ...this.currentState };
  }

  /** Check consent for a category */
  hasConsent(category: ConsentCategory): boolean {
    return this.currentState[category] ?? false;
  }

  /** 
   * Grant consent for categories
   * @param categories - Single category or array of categories
   * @param triggerElementId - Optional element ID that triggered the request (for priority loading)
   */
  grantConsent(categories: ConsentCategory | ConsentCategory[], triggerElementId?: string | null): void {
    const cats = Array.isArray(categories) ? categories : [categories];
    const oldState = { ...this.currentState };
    
    for (const cat of cats) {
      this.currentState[cat] = true;
      
      // Store trigger element ID for the grant event
      if (triggerElementId) {
        const existing = this.pendingConsentResolvers.get(cat);
        if (existing) {
          existing.triggerElementId = triggerElementId;
        } else {
          this.pendingConsentResolvers.set(cat, { 
            resolve: () => {}, 
            triggerElementId 
          });
        }
      }
    }
    
    // Save and log
    this.saveState();
    this.logToBackend();
    
    // Emit events for newly granted categories
    for (const cat of cats) {
      if (!oldState[cat]) {
        const pending = this.pendingConsentResolvers.get(cat);
        this.emitGrant(cat, pending?.triggerElementId ?? triggerElementId ?? null);
        if (pending) {
          pending.resolve(true);
          this.pendingConsentResolvers.delete(cat);
        }
        // Load scripts for newly granted category
        this.loadScriptsForCategory(cat);
      }
    }
    
    this.emit('rd:consent:changed', this.currentState);
    this.notifySubscribers(this.currentState);
  }

  /** Revoke consent for categories */
  async revokeConsent(categories: ConsentCategory | ConsentCategory[]): Promise<void> {
    const cats = Array.isArray(categories) ? categories : [categories];
    const oldState = { ...this.currentState };
    let hasRevokes = false;
    
    for (const cat of cats) {
      if (cat === 'necessary') continue; // Can't revoke necessary
      if (oldState[cat]) {
        this.currentState[cat] = false;
        hasRevokes = true;
        this.emitRevoke(cat);
      }
    }
    
    if (hasRevokes) {
      this.saveState();
      this.emit('rd:consent:changed', this.currentState);
      this.notifySubscribers(this.currentState);
      
      // Wait for backend logging to complete before reload
      await this.logToBackend();
      
      // Reload page to unload scripts that were loaded with revoked consent
      console.info('[ConsentManager] Consent revoked, reloading page');
      window.location.reload();
    }
  }

  /** Subscribe to consent changes - replays current state immediately */
  subscribe(fn: ConsentSubscriber): () => void {
    this.subscribers.add(fn);
    
    // Replay current state immediately
    if (this.ready) {
      fn(this.currentState);
    }
    
    return () => {
      this.subscribers.delete(fn);
    };
  }

  /** Get the consent UUID for this browser */
  getUUID(): string {
    return this.uuid;
  }

  /**
   * Show consent dialog for a specific service
   * Returns a promise that resolves to true if consent granted, false if declined
   * This creates an in-content blocker dialog, not the full CMP modal
   * 
   * @param service - Service ID (e.g., 'mapbox', 'youtube')
   * @param triggerElementId - Optional element ID that triggered the request (for priority loading)
   */
  showConsentDialog(service: string, triggerElementId: string | null = null): Promise<boolean> {
    const category = getServiceCategory(service) as ConsentCategory;
    
    // If already have consent for the category, resolve immediately
    if (this.hasConsent(category)) {
      // Still emit grant event with triggerElementId for priority loading
      this.emitGrant(category, triggerElementId);
      return Promise.resolve(true);
    }

    // Inject styles on first use
    injectBlockerStyles();

    return new Promise((resolve) => {
      // Store resolver so we can resolve when consent is granted via adapter
      this.pendingConsentResolvers.set(category, { resolve, triggerElementId });

      const blockerConfig = getBlockerConfig(service);
      const serviceInfo = getService(service);
      
      // Create modal overlay with 2-level explanation
      const overlay = document.createElement('div');
      overlay.className = 'rd-consent-dialog-overlay';
      overlay.innerHTML = createBlockerHtml(blockerConfig, serviceInfo, category);

      document.body.appendChild(overlay);

      const cleanup = () => {
        overlay.remove();
        this.pendingConsentResolvers.delete(category);
      };

      // Handle accept - grants the CATEGORY, not just the service
      overlay.querySelector('.rd-consent-dialog-accept')?.addEventListener('click', () => {
        cleanup();
        this.grantConsent(category, triggerElementId);
        // Don't resolve here - the adapter subscription will resolve when consent is saved
      });

      // Handle decline
      overlay.querySelector('.rd-consent-dialog-decline')?.addEventListener('click', () => {
        cleanup();
        resolve(false);
      });

      // Handle click outside
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          cleanup();
          resolve(false);
        }
      });

      // Handle escape key
      const escHandler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          cleanup();
          document.removeEventListener('keydown', escHandler);
          resolve(false);
        }
      };
      document.addEventListener('keydown', escHandler);
    });
  }

  /**
   * Create an inline blocker element for embedding in content
   * Uses the same design as the popup dialog
   * 
   * @param service - Service ID (e.g., 'mapbox', 'youtube')
   * @param triggerElementId - Element ID for priority loading
   */
  createInlineBlocker(service: string, triggerElementId: string | null = null): HTMLElement {
    // Inject styles on first use
    injectBlockerStyles();
    
    const category = getServiceCategory(service) as ConsentCategory;
    const blockerConfig = getBlockerConfig(service);
    const serviceInfo = getService(service);
    
    const blocker = document.createElement('div');
    blocker.className = 'rd-consent-blocker';
    blocker.innerHTML = createBlockerHtml(blockerConfig, serviceInfo, category, true);
    
    // Handle accept - grants the CATEGORY
    blocker.querySelector('.rd-consent-dialog-accept')?.addEventListener('click', () => {
      this.grantConsent(category, triggerElementId);
    });
    
    // Handle decline - just hide the blocker (content stays blocked)
    blocker.querySelector('.rd-consent-dialog-decline')?.addEventListener('click', () => {
      blocker.classList.add('rd-consent-blocker-dismissed');
    });
    
    return blocker;
  }

  /**
   * Set the handler for showing the consent modal.
   * Called by bootstrap after lazy-loading the consent modal.
   */
  setShowCMPHandler(handler: () => void): void {
    this.showCMPHandler = handler;
  }

  /**
   * Show the consent management modal.
   * Public API for opening consent settings from anywhere.
   */
  showCMP(): void {
    if (this.showCMPHandler) {
      this.showCMPHandler();
    } else {
      console.warn('[ConsentManager] showCMP handler not yet initialized');
    }
  }
}

/**
 * Create unified blocker HTML with 2-level explanation:
 * Level 1: Service-specific reason (from service blocker config)
 * Level 2: Category explanation (we activate the whole category)
 */
function createBlockerHtml(
  blockerConfig: ReturnType<typeof getBlockerConfig>, 
  serviceInfo: ReturnType<typeof getService>,
  category: ConsentCategory,
  isInline = false
): string {
  // Category-specific button text and explanation
  const categoryConfig: Record<string, { button: string; explanation: string }> = {
    'external-media': {
      button: 'Externe Inhalte laden',
      explanation: 'Wir benötigen deine Zustimmung, um externe Inhalte (wie Karten, Videos) zu laden.',
    },
    'marketing': {
      button: 'Marketing zustimmen',
      explanation: 'Wir benötigen deine Zustimmung für Marketing und personalisierte Werbung.',
    },
    'analytics': {
      button: 'Statistik zustimmen',
      explanation: 'Wir benötigen deine Zustimmung für anonyme Nutzungsstatistiken.',
    },
    'functionality': {
      button: 'Funktionen aktivieren',
      explanation: 'Wir benötigen deine Zustimmung für erweiterte Funktionen.',
    },
  };
  
  const catConfig = categoryConfig[category] || { button: 'Zustimmen', explanation: '' };
  
  // Level 1: Service-specific reason from blocker config
  const blocker = serviceInfo?.blocker || blockerConfig;
  const serviceReason = blocker?.description || `Um ${blockerConfig.title} anzuzeigen, laden wir Inhalte von ${serviceInfo?.name || blockerConfig.serviceName}.`;
  
  const dialogClass = isInline ? 'rd-consent-dialog rd-consent-dialog-inline' : 'rd-consent-dialog';
  
  return `
    <div class="${dialogClass}">
      <div class="rd-consent-dialog-body">
        <p class="rd-consent-level1">${serviceReason}</p>
        <p class="rd-consent-level2">${catConfig.explanation}</p>
      </div>
      <div class="rd-consent-dialog-actions">
        <button class="rd-consent-dialog-accept">${catConfig.button}</button>
        <button class="rd-consent-dialog-decline">Abbrechen</button>
      </div>
      <div class="rd-consent-dialog-footer">
        <span class="rd-consent-withdraw-hint">Du kannst deine Entscheidung jederzeit widerrufen.</span>
        <a href="${SITE_CONFIG.links.privacy}" target="_blank" rel="noopener">Details in der Datenschutzerklärung</a>
      </div>
    </div>
  `;
}

// Singleton instance
export const consentManager = new RDConsentManager();
